import { CaptchaType, Theme } from '@logto/schemas';
import { useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { verifyCaptcha } from '@/apis/experience';
import useToast from '@/hooks/use-toast';

import PageContext from '../PageContextProvider/PageContext';

import CaptchaContext, { type CaptchaContextType } from './CaptchaContext';
import { scriptId } from './constant';
import { getScript } from './utils';

type Props = {
  readonly children: React.ReactNode;
};

type AliyunCaptchaPromiseState = {
  resolve: (value: string | undefined) => void;
  reject: (reason?: Error) => void;
  token?: string;
  settled: boolean;
  attemptId: number;
};

const isAliyunCaptchaFailoverParam = (captchaVerifyParam: string) => {
  try {
    const payload: unknown = JSON.parse(captchaVerifyParam);

    return (
      typeof payload === 'object' &&
      payload !== null &&
      'failover' in payload &&
      payload.failover === 'T'
    );
  } catch {
    return false;
  }
};

const CaptchaContextProvider = ({ children }: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);
  const widgetRef = useRef<HTMLDivElement>(null);
  const executeCaptchaRef = useRef<CaptchaContextType['executeCaptcha']>();
  const aliyunCaptchaPromiseRef = useRef<AliyunCaptchaPromiseState>();
  const aliyunCaptchaScriptPromiseRef = useRef<Promise<void>>();
  const hasExecutedAliyunCaptchaRef = useRef(false);
  const shouldReloadAliyunCaptchaRef = useRef(false);
  const aliyunCaptchaAttemptIdRef = useRef(0);
  const { setToast } = useToast();
  const { t } = useTranslation();

  const captchaPolicy = experienceSettings?.captchaPolicy;
  const captchaConfig = experienceSettings?.captchaConfig;

  const isCaptchaRequired = Boolean(captchaPolicy?.enabled);

  const verifyAliyunCaptcha = useCallback(async (captchaToken: string) => {
    const result = await verifyCaptcha(captchaToken);

    return result.success;
  }, []);

  const cleanUpAliyunCaptcha = useCallback((removeScript = false) => {
    // Per the official Aliyun CAPTCHA 2.0 integration example, the leftover elements created
    // by the SDK must be removed before re-initializing (or on unmount), otherwise stale
    // callbacks may fire on retries. The SDK instance itself exposes no destroy method
    // (verified against the production `AliyunCaptcha.js` build).
    document.querySelector('#aliyunCaptcha-mask')?.remove();
    document.querySelector('#aliyunCaptcha-window-popup')?.remove();

    if (widgetRef.current) {
      // eslint-disable-next-line @silverhand/fp/no-mutation
      widgetRef.current.innerHTML = '';
    }

    if (!removeScript) {
      return;
    }

    document.querySelector(`#${scriptId}`)?.remove();
    for (const script of document.querySelectorAll(
      'script[src*="captcha-frontend/FeiLin"], script[src*="captcha-frontend/dynamicJS"]'
    )) {
      script.remove();
    }
    // eslint-disable-next-line @silverhand/fp/no-delete
    delete window.initAliyunCaptcha;
    // eslint-disable-next-line @silverhand/fp/no-delete
    delete window.AliyunCaptchaConfig;
    // eslint-disable-next-line @silverhand/fp/no-mutation
    aliyunCaptchaScriptPromiseRef.current = undefined;
  }, []);

  const resetCaptcha = useCallback(() => {
    if (captchaConfig?.type !== CaptchaType.AliyunCaptcha) {
      return;
    }

    // Avoid tearing down the Aliyun SDK while its internal async callbacks are still running.
    // The next execution will fully reload the SDK before re-initializing the widget.
    // eslint-disable-next-line @silverhand/fp/no-mutation
    shouldReloadAliyunCaptchaRef.current = true;
    // eslint-disable-next-line @silverhand/fp/no-mutation
    aliyunCaptchaPromiseRef.current = undefined;
  }, [captchaConfig?.type]);

  const loadCaptchaScript = useCallback(
    async (forceReload = false) => {
      if (!isCaptchaRequired || !captchaConfig) {
        return;
      }

      if (forceReload) {
        cleanUpAliyunCaptcha(true);
      }

      if (captchaConfig.type === CaptchaType.AliyunCaptcha && window.initAliyunCaptcha) {
        return;
      }

      if (document.querySelector(`#${scriptId}`)) {
        return aliyunCaptchaScriptPromiseRef.current;
      }

      const scriptPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');

        /* eslint-disable @silverhand/fp/no-mutation */
        if (captchaConfig.type === CaptchaType.AliyunCaptcha) {
          window.AliyunCaptchaConfig = {
            region: 'cn',
            prefix: captchaConfig.prefix,
          };
        }

        script.src = getScript(captchaConfig);
        script.id = scriptId;
        script.async = true;
        script.addEventListener('load', () => {
          resolve();
        });
        script.addEventListener('error', () => {
          reject(new Error('Failed to load CAPTCHA script'));
        });
        /* eslint-enable @silverhand/fp/no-mutation */

        document.body.append(script);
      });

      /* eslint-disable @silverhand/fp/no-mutation */
      aliyunCaptchaScriptPromiseRef.current = scriptPromise;
      /* eslint-enable @silverhand/fp/no-mutation */

      return scriptPromise;
    },
    [captchaConfig, cleanUpAliyunCaptcha, isCaptchaRequired]
  );

  const initCaptcha = useCallback(() => {
    void loadCaptchaScript();
  }, [loadCaptchaScript]);

  const rejectAliyunCaptcha = useCallback(
    (attemptId: number) => {
      const promiseState = aliyunCaptchaPromiseRef.current;

      if (!promiseState || promiseState.settled || promiseState.attemptId !== attemptId) {
        return;
      }

      // eslint-disable-next-line @silverhand/fp/no-mutation
      promiseState.settled = true;
      setToast(t('error.captcha_verification_failed'));
      promiseState.reject(new Error('Alibaba Cloud CAPTCHA 2.0 verification failed'));
    },
    [setToast, t]
  );

  const reloadAliyunCaptcha = useCallback(() => {
    // Defer cleanup until after the SDK finishes the current callback stack. Its own refresh path
    // throws in the deployed embed mode, so we rebuild the widget instead.
    window.setTimeout(() => {
      void (async () => {
        try {
          await executeCaptchaRef.current?.();
        } catch {
          // The caller already received the CAPTCHA failure. This background reload only restores
          // the widget for the next attempt.
        }
      })();
    });
  }, []);

  const resolveAliyunCaptcha = useCallback((attemptId: number) => {
    const promiseState = aliyunCaptchaPromiseRef.current;

    if (!promiseState || promiseState.settled || promiseState.attemptId !== attemptId) {
      return;
    }

    // eslint-disable-next-line @silverhand/fp/no-mutation
    promiseState.settled = true;
    promiseState.resolve(undefined);
  }, []);

  const executeCaptcha = useCallback(async () => {
    if (!isCaptchaRequired || !captchaConfig) {
      return;
    }

    if (captchaConfig.type === CaptchaType.Turnstile) {
      return new Promise<string | undefined>((resolve, reject) => {
        if (!window.turnstile || !widgetRef.current) {
          resolve(undefined);
          return;
        }

        // Clear the dom element first
        // eslint-disable-next-line @silverhand/fp/no-mutation
        widgetRef.current.innerHTML = '';

        window.turnstile.render(widgetRef.current, {
          sitekey: captchaConfig.siteKey,
          theme: theme === Theme.Light ? 'light' : 'dark',
          callback: (token: string) => {
            resolve(token);
          },
          'error-callback': (errorCode) => {
            setToast(t('error.captcha_verification_failed'));
            reject(new Error(`Turnstile error: ${errorCode}`));
          },
          size: 'flexible',
        });
      });
    }

    if (captchaConfig.type === CaptchaType.AliyunCaptcha) {
      const widget = widgetRef.current;

      if (!widget) {
        return;
      }

      try {
        await loadCaptchaScript(
          hasExecutedAliyunCaptchaRef.current || shouldReloadAliyunCaptchaRef.current
        );
      } catch {
        setToast(t('error.captcha_verification_failed'));
        throw new Error('Failed to load CAPTCHA script');
      }

      const { initAliyunCaptcha } = window;

      if (!initAliyunCaptcha) {
        return;
      }

      // eslint-disable-next-line @silverhand/fp/no-mutation
      hasExecutedAliyunCaptchaRef.current = true;
      // eslint-disable-next-line @silverhand/fp/no-mutation
      shouldReloadAliyunCaptchaRef.current = false;

      return new Promise<string | undefined>((resolve, reject) => {
        const attemptId = aliyunCaptchaAttemptIdRef.current + 1;

        // eslint-disable-next-line @silverhand/fp/no-mutation
        aliyunCaptchaAttemptIdRef.current = attemptId;
        // eslint-disable-next-line @silverhand/fp/no-mutation
        aliyunCaptchaPromiseRef.current = { resolve, reject, settled: false, attemptId };
        // eslint-disable-next-line @silverhand/fp/no-mutation
        widget.id = 'aliyun-captcha-widget';

        initAliyunCaptcha({
          SceneId: captchaConfig.sceneId,
          mode: 'embed',
          element: '#aliyun-captcha-widget',
          captchaVerifyCallback: async (captchaVerifyParam) => {
            const currentPromiseState = aliyunCaptchaPromiseRef.current;

            if (
              !currentPromiseState ||
              currentPromiseState.settled ||
              currentPromiseState.attemptId !== attemptId
            ) {
              return;
            }

            // eslint-disable-next-line @silverhand/fp/no-mutation
            aliyunCaptchaPromiseRef.current = {
              ...currentPromiseState,
              resolve,
              reject,
              token: captchaVerifyParam,
              attemptId,
            };

            if (isAliyunCaptchaFailoverParam(captchaVerifyParam)) {
              return { captchaResult: false, bizResult: false };
            }

            const success = await verifyAliyunCaptcha(captchaVerifyParam).catch(() => false);

            if (!success) {
              rejectAliyunCaptcha(attemptId);
              // Avoid returning a failed business result to Aliyun SDK because its internal refresh
              // path throws and leaves the widget stuck at "verifying". Rebuild the widget ourselves.
              reloadAliyunCaptcha();
              return;
            }

            return { captchaResult: true, bizResult: true };
          },
          success: () => {
            resolveAliyunCaptcha(attemptId);
          },
          fail: () => {
            rejectAliyunCaptcha(attemptId);
          },
          onBizResultCallback: (success) => {
            if (success) {
              resolveAliyunCaptcha(attemptId);
            }
          },
        });
      });
    }

    if (!window.grecaptcha?.enterprise) {
      return;
    }

    return window.grecaptcha.enterprise.execute(captchaConfig.siteKey, {
      action: 'interaction',
    });
  }, [
    isCaptchaRequired,
    captchaConfig,
    theme,
    setToast,
    t,
    verifyAliyunCaptcha,
    loadCaptchaScript,
    resolveAliyunCaptcha,
    rejectAliyunCaptcha,
    reloadAliyunCaptcha,
  ]);

  // eslint-disable-next-line @silverhand/fp/no-mutation
  executeCaptchaRef.current = executeCaptcha;

  useEffect(() => {
    initCaptcha();
  }, [initCaptcha]);

  useEffect(
    () => () => {
      // Clean up the Aliyun captcha widget elements on unmount.
      cleanUpAliyunCaptcha(true);
    },
    [cleanUpAliyunCaptcha]
  );

  const captchaContext = useMemo<CaptchaContextType>(
    () => ({
      isCaptchaRequired,
      executeCaptcha,
      resetCaptcha,
      captchaConfig,
      widgetRef,
    }),
    [isCaptchaRequired, executeCaptcha, resetCaptcha, captchaConfig, widgetRef]
  );

  return <CaptchaContext.Provider value={captchaContext}>{children}</CaptchaContext.Provider>;
};

export default CaptchaContextProvider;
